import { useState } from "react";
import { Drawer, Descriptions, Tag, Divider, Empty, Button, Modal } from "antd";
import dayjs from "dayjs";
import styles from "./drawer.module.scss";
import GiveBundle from "../GiveBundle.js";
import ConfirmModal from "../../../../components/modal/ConfirmModal.js";
import api from "../../../../api/index.js";
import { useToast } from "../../../../components/toast/index.js";
import CustomBtn from "../../../../components/btn/CustomBtn.js";
import CustomInput from "../../../../components/input/CustomInput.js";

const DetailDrawer = ({ open, onClose, record, list, trigger }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  // state
  const [visible, setVisible] = useState(false);
  const [extraDays, setExtraDays] = useState(0);
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  if (!record) return null;

  const bundles = record.UserBundles || [];

  const triggerFunction = () => {
    setShowModal(false);
    setSelectedId("");
    trigger?.();
    onClose?.();
  };

  const deleteHandler = async () => {
    // console.log("selectedId =-->", selectedId);
    const res = await api.cancel_bundle(selectedId);
    if (res.status === 200) {
      triggerFunction();
      toast("Амжилттай цуцлаллаа", "success");
    } else {
      toast(res.data.message || "Алдаа гарлаа", "error");
    }
  };

  const handleClose = () => {
    setVisible(false);
    setExtraDays(0);
    triggerFunction?.();
  };

  const handleSave = async () => {
    setSaving(true);
    const res = await api.extend_bundle(selectedId, { extra_days: extraDays });
    // console.log("extend bundle res --->", res);
    if (res.status === 200) {
      toast("Амжилттай", "success");
      handleClose();
    } else {
      toast(res.data.message || "Алдаа гарлаа", "error");
    }
    setSaving(false);
  };

  return (
    <>
      <Drawer
        title="Хэрэглэгчийн дэлгэрэнгүй"
        placement="right"
        onClose={onClose}
        open={open}
        width={800}
        extra={
          record.active && (
            <GiveBundle
              userId={record.id}
              list={list}
              trigger={triggerFunction}
            />
          )
        }
      >
        <div className={styles.wrapper}>
          {/* --- Хэрэглэгчийн үндсэн мэдээлэл --- */}
          <Descriptions
            bordered
            size="small"
            column={2}
            style={{ borderRadius: 0 }}
            styles={{
              label: {
                width: 100,
                fontSize: 12,
              },
              content: {
                fontSize: 13,
                fontWeight: 500,
                color: "#000",
              },
            }}
          >
            <Descriptions.Item label="Email">
              {record.email || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Нэр">
              {record.username || "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Утас">
              {record.phone_number || "-"}
            </Descriptions.Item>

            <Descriptions.Item label="Бүртгүүлсэн огноо">
              {record.created_at
                ? dayjs(record.created_at).format("YYYY-MM-DD HH:mm")
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Төлөв">
              <Tag color={record.active ? "green" : "default"}>
                {record.active ? "Идэвхтэй" : "Идэвхгүй"}
              </Tag>
            </Descriptions.Item>
          </Descriptions>

          {/* === Багцууд === */}
          <Divider orientation="left" plain style={{ borderColor: "#ccc" }}>
            <div className={styles.title}>Хэрэглэгчийн багц</div>
          </Divider>

          {bundles.length === 0 && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={"Одоогоор багцгүй."}
            />
          )}

          {bundles.map((b) => {
            const remainCredits =
              b.credits_total != null
                ? Math.max(0, (b.credits_total || 0) - (b.credits_used || 0))
                : null;

            return (
              <div key={b.id} className={styles.bundleWrapper}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex gap-1">
                    <Tag color={b.program === "YOGA" ? "blue" : "cyan"}>
                      {b.program}
                    </Tag>
                    <div className={styles.title}>{b.Bundle?.name || ""}</div>
                  </div>
                  {b.status === "active" && (
                    <div className="d-flex gap-2">
                      <Button
                        variant="dashed"
                        color="blue"
                        size="small"
                        style={{
                          paddingLeft: 20,
                          paddingRight: 20,
                          fontSize: 12,
                        }}
                        onClick={() => {
                          setVisible(true);
                          setSelectedId(b.id);
                        }}
                      >
                        Сунгах
                      </Button>
                      <Button
                        variant="dashed"
                        color="danger"
                        size="small"
                        style={{
                          paddingLeft: 20,
                          paddingRight: 20,
                          fontSize: 12,
                        }}
                        onClick={() => {
                          setShowModal(true);
                          setSelectedId(b.id);
                        }}
                      >
                        Багц цуцлах
                      </Button>
                    </div>
                  )}
                </div>
                <Divider
                  style={{
                    marginTop: 15,
                    marginBottom: 15,
                    borderColor: "#eee",
                  }}
                />

                <div className={styles.detail}>
                  <div>
                    <span>Эхлэх:</span>{" "}
                    {dayjs(b.starts_at).format("YYYY-MM-DD HH:mm")}
                  </div>
                  <div>
                    <span>Дуусах:</span>{" "}
                    {dayjs(b.expires_at).format("YYYY-MM-DD HH:mm")}
                  </div>
                  <div>
                    <span>Төлөв:</span>{" "}
                    <Tag color={b.status === "active" ? "green" : "default"}>
                      {b.status}
                    </Tag>
                  </div>
                  {b.credits_total != null && (
                    <div>
                      <span>Кредит:</span> {b.credits_used} / {b.credits_total}{" "}
                      ({remainCredits} үлдсэн)
                    </div>
                  )}
                  {b.Bundle.restrict_time != null && (
                    <div>
                      <span>Орох цаг:</span> {b.Bundle.restrict_time} цаг
                    </div>
                  )}
                  {b.Bundle && (
                    <div>
                      <span>Үнэ:</span> {b.Bundle.price.toLocaleString()}₮ /{" "}
                      <span>Хүчинтэй:</span> {b.Bundle.valid_days} хоног
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Drawer>

      <ConfirmModal
        showModal={showModal}
        setShowModal={setShowModal}
        submitHandler={deleteHandler}
        cancelHandler={() => {
          setSelectedId("");
        }}
        variant="error"
        title="Итгэлтэй байна уу?"
        description={
          <>
            Хэрэглэгчийн '
            <span style={{ fontWeight: 600, color: "#000" }}>Багц цуцлах</span>'
            үйлдэл хийснээр буцаах боломжгүй тул сайтар нягтална уу!
          </>
        }
      />

      <Modal
        open={visible}
        closable={false}
        style={{ top: 200, zIndex: 99 }}
        width={420}
        title={"Багц сунгах"}
        footer={
          <div className="d-flex gap-2 justify-content-end">
            <CustomBtn type="secondary" title="Болих" onClick={handleClose} />
            <CustomBtn
              title={"Хадгалах"}
              onClick={handleSave}
              // disabled={!saving && Number(extraDays) > 0 ? false : true}
              disabled={Number(extraDays) <= 0 || saving}
              loading={saving}
            />
          </div>
        }
      >
        <CustomInput
          title={"Сунгах хоног оруулна уу. / 3, 6, 7 ... /"}
          required
          placeholder="-"
          type="number"
          value={extraDays}
          onChange={(e) => setExtraDays(e?.target?.value)}
        />
      </Modal>
    </>
  );
};

export default DetailDrawer;
